import React from 'react';
import PropTypes from 'prop-types';
import {Link} from 'react-router-dom';
import styled from 'styled-components';
import Layout from './Layout';
import VehicleTelematics from './VehicleTelematics';
import DeviceReadings from './DeviceReadings';
import {createIdempotencyKey, getVegaCommand, getVegaDevice, getVegaMessages, previewVegaCommand, sendVegaCommand} from '../../api/rental';
import {ActionButton, Actions, Badge, Card, CardTitle, ErrorText, Field, Select, Tab, Tabs, Textarea} from './styles';

const templates = [
  ['Открыть автомобиль', 'unlock'], ['Закрыть автомобиль', 'lock'],
  ['Центральный замок', 'get-param 0B0E 0'], ['Передняя левая дверь', 'get-param 0B0F 0'],
  ['Передняя правая дверь', 'get-param 0B10 0'], ['Задняя правая дверь', 'get-param 0B11 0'],
  ['Задняя левая дверь', 'get-param 0B12 0'], ['Багажник', 'get-param 0B14 0'],
  ['Скорость (параметр 103)', 'get-param 0067 0'], ['IMEI', 'get-param 0005 0'],
  ['Одометр (2800)', 'get-param 0AF0 0'], ['Скорость CAN (2801)', 'get-param 0AF1 0'],
  ['Топливо / заряд (2803)', 'get-param 0AF3 0'], ['Запас хода (2804)', 'get-param 0AF4 0'],
  ['Ремень (2848)', 'get-param 0B20 0'], ['Аварийка (2847)', 'get-param 0B1F 0'],
];
const statusNames = {pending: 'В очереди', in_progress: 'Выполняется', success: 'Устройство подтвердило выполнение', error: 'Ошибка'};
const time = value => value ? new Date(value).toLocaleString('ru-RU') : 'Нет данных';
const pause = ms => new Promise(resolve => setTimeout(resolve, ms));

export function ConsoleContent({authorization, role, imei}) {
  const [device, setDevice] = React.useState(null);
  const [messages, setMessages] = React.useState([]);
  const [cursor, setCursor] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  const [text, setText] = React.useState('get-param 0B0E 0');
  const [preview, setPreview] = React.useState(null);
  const [result, setResult] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [unknown, setUnknown] = React.useState(null);
  const [tab, setTab] = React.useState('console');
  const [fullscreen, setFullscreen] = React.useState(false);
  const [hideHeartbeat, setHideHeartbeat] = React.useState(false);
  const alive = React.useRef(true);
  const request = React.useRef(0);
  const logElement = React.useRef(null);
  const scrollLatest = React.useRef(true);
  React.useEffect(() => {
    if (scrollLatest.current && logElement.current) {
      logElement.current.scrollTop = logElement.current.scrollHeight;
      scrollLatest.current = false;
    }
  }, [messages, fullscreen, tab, hideHeartbeat]);
  React.useEffect(() => () => { alive.current = false; }, []);
  React.useEffect(() => {
    const escape = event => { if (event.key === 'Escape') { setFullscreen(false); } };
    document.addEventListener('keydown', escape);
    return () => document.removeEventListener('keydown', escape);
  }, []);
  const refresh = React.useCallback(async (before) => {
    request.current += 1;
    const version = request.current;
    setLoading(true); setError('');
    try {
      const [info, log] = await Promise.all([getVegaDevice(authorization, imei), getVegaMessages(authorization, imei, before)]);
      if (!alive.current || request.current !== version) { return; }
      setDevice(info);
      scrollLatest.current = !before;
      setMessages(previous => (before ? previous.concat(log.items.filter(item => !previous.some(old => old.id === item.id))) : log.items.slice()).slice(0, 1000));
      setCursor(log.next_cursor);
    } catch (e) { if (alive.current && request.current === version) { setError(e.message); } }
    finally { if (alive.current && request.current === version) { setLoading(false); } }
  }, [authorization, imei]);
  React.useEffect(() => { refresh(); }, [refresh]);
  React.useEffect(() => {
    let inFlight = false;
    const timer = setInterval(async () => {
      if (document.hidden || inFlight) return;
      inFlight = true;
      try {
        const info = await getVegaDevice(authorization, imei);
        if (alive.current) setDevice(info);
      } catch (e) { /* Журнал остаётся доступен; явное обновление покажет ошибку. */ }
      finally { inFlight = false; }
    }, 10000);
    return () => clearInterval(timer);
  }, [authorization, imei]);

  const check = async () => {
    setBusy(true); setError('');
    try {
      const info = await previewVegaCommand(authorization, imei, text);
      if (alive.current) { setPreview(info); }
    } catch (e) { if (alive.current) { setError(e.message); } }
    finally { if (alive.current) { setBusy(false); } }
  };
  const send = async previous => {
    const operation = previous || {text, key: createIdempotencyKey('vega')};
    setBusy(true); setError(''); setResult(null);
    try {
      let answer = operation.id ? await getVegaCommand(authorization, imei, operation.id)
        : await sendVegaCommand(authorization, imei, operation.text, operation.key);
      operation.id = answer.id;
      if (!operation.id) { throw new Error('Ответ не содержит ID команды'); }
      const deadline = Date.now() + 6000;
      while (alive.current && ['pending', 'in_progress'].includes(answer.status) && Date.now() < deadline) {
        // Последовательный polling одной команды: параллельные запросы не нужны.
        // eslint-disable-next-line no-await-in-loop
        await pause(250);
        // eslint-disable-next-line no-await-in-loop
        answer = await getVegaCommand(authorization, imei, operation.id);
      }
      if (!alive.current) { return; }
      setResult(answer);
      setUnknown(['pending', 'in_progress'].includes(answer.status) ? operation : null);
    } catch (e) {
      if (!alive.current) { return; }
      setError(e.message);
      // 4xx до создания — известный отказ; при потере ответа не создаём новый ключ.
      setUnknown(operation.id || !e.status || e.status >= 500 ? operation : null);
    } finally {
      await pause(2000);
      if (alive.current) { await refresh(); setBusy(false); }
    }
  };
  const change = value => { setText(value); setPreview(null); };
  return <>
    {error && <ErrorText role="alert">{error}</ErrorText>}
    {loading && <p role="status">Обновление устройства…</p>}
    {device && <p><Link to={`/rental/vehicles?vehicle_id=${device.vehicle.id}`}>
      {device.vehicle.brand} {device.vehicle.model} · {device.vehicle.license_plate}</Link>{' · '}
    <Badge success={device.device.online}>{device.device.online ? 'На связи' : 'Не на связи'}</Badge>{' · '}
    Последняя связь: {time(device.device.last_seen_at)}</p>}
    <Tabs aria-label="Разделы устройства">
      <Tab active={tab === 'console'} onClick={() => setTab('console')}>Сообщения и команды</Tab>
      <Tab active={tab === 'telemetry'} onClick={() => setTab('telemetry')}>Телеметрия и настройки</Tab>
    </Tabs>
    <div hidden={tab !== 'console'} style={{marginTop: 20}}>
      <Card>
        <CardTitle>Последние показания</CardTitle>
        <DeviceReadings readings={(device && device.device.readings) || {}} />
      </Card>
      <LogCard fullscreen={fullscreen} aria-label="Журнал VEGA">
        <Actions>
          <CardTitle>Сообщения VEGA</CardTitle>
          <ActionButton disabled={loading} onClick={() => refresh()}>Обновить</ActionButton>
          <ActionButton onClick={() => setFullscreen(!fullscreen)}>{fullscreen ? 'Свернуть журнал' : 'На весь экран'}</ActionButton>
          <ActionButton aria-pressed={hideHeartbeat} onClick={() => { scrollLatest.current = true; setHideHeartbeat(!hideHeartbeat); }}>
            {hideHeartbeat ? 'Показать ping/pong' : 'Скрыть ping/pong'}
          </ActionButton>
          {cursor && messages.length < 1000 && <ActionButton disabled={loading} onClick={() => refresh(cursor)}>Загрузить ещё</ActionButton>}
        </Actions>
        <p>Загружено {messages.length} из последних 1000 сообщений. Более ранняя история сохранена на сервере.</p>
        <Log ref={logElement} fullscreen={fullscreen} tabIndex={0}>
          {!messages.length && <p>Сообщений пока нет.</p>}
          {messages.filter(item => !hideHeartbeat || ![10, 11].includes(item.message_type)).slice().reverse().map(item => <LogLine key={item.id} direction={item.direction}>
            <small>{item.received_at ? new Date(item.received_at).toISOString() : 'Нет времени'} [{item.direction}] {item.raw_hex}</small>
            <div>{item.description.replace(/ID=(\d+)/, (match) => `${match}${({lock: '(закрыть дверь)', unlock: '(открыть дверь)'})[item.action] || ''}`)}, сессия {item.session_id}</div>
          </LogLine>)}
        </Log>
      </LogCard>
      <Card style={{marginTop: 20}}>
        <CardTitle>Отправить команду</CardTitle>
        <Field>Шаблон<Select aria-label="Шаблон команды" disabled={busy || !!unknown} value="" onChange={e => change(e.target.value)}>
          <option value="">Выберите шаблон</option>
          {templates.map(([label, command]) => <option key={command} value={command}>{label}</option>)}
        </Select></Field>
        <Field>Команда<Textarea aria-label="Команда VEGA" value={text} disabled={busy || !!unknown} onChange={e => change(e.target.value)} /></Field>
        <p><code>get-param HHHH SUB</code> · <code>set-param HHHH SUB uint8 VALUE</code><br />
          <code>raw-command CODE HEX_ARGS</code> — код команды в hex, аргументы в hex, пробелы допустимы.
          ID, длину и CRC формирует сервис. Готовые TCP-кадры не принимаются.</p>
        {role !== 'administrator' && <p>Произвольная запись доступна только администратору. Чтение и штатные команды дверей доступны оператору.</p>}
        <Actions>
          <ActionButton disabled={busy || !!unknown || !text.trim()} onClick={check}>Проверить команду</ActionButton>
          <ActionButton disabled={busy || !!unknown || !text.trim()} onClick={() => send()}>Отправить</ActionButton>
          {unknown && <ActionButton disabled={busy} onClick={() => send(unknown)}>Проверить результат команды</ActionButton>}
        </Actions>
        <p>Проверка необязательна: показывает пакет без отправки устройству.</p>
        {busy && <p role="status">Запрос выполняется…</p>}
        {unknown && <p role="status">Окончательный результат пока неизвестен. Проверка использует исходный ключ или ID команды, без автоматических повторов.</p>}
        {preview && <pre style={{whiteSpace: 'pre-wrap', overflowWrap: 'anywhere'}}>
          {preview.description}{'\n'}{preview.frame_preview_hex}{'\n'}Предпросмотр с ID=0; при отправке сервис назначит новый ID и CRC.
        </pre>}
        {result && <div role="status"><p>{statusNames[result.status]} · {result.id}{result.error_code && ` · ${result.error_code}`}</p>
          <p>Подтверждение команды не заменяет показания датчика замка.</p>
          <pre style={{whiteSpace: 'pre-wrap', overflowWrap: 'anywhere'}}>{JSON.stringify({
            sent_at: result.sent_at, request_hex: result.request_hex, response_at: result.response_at,
            response_hex: result.response_hex, decoded_response: result.decoded_response,
          }, null, 2)}</pre></div>}
      </Card>
    </div>
    {device && tab === 'telemetry' && <Card style={{marginTop: 20}}>
      <VehicleTelematics key={device.vehicle.id} authorization={authorization} vehicleId={device.vehicle.id} />
    </Card>}
  </>;
}

ConsoleContent.propTypes = {authorization: PropTypes.string.isRequired, role: PropTypes.string.isRequired, imei: PropTypes.string.isRequired};

const LogCard = styled(Card)`
  margin-top: 20px;
  ${p => p.fullscreen && 'position: fixed; inset: 16px; z-index: 10000; margin: 0; display: flex; flex-direction: column;'}
`;
const Log = styled.div`
  height: ${p => p.fullscreen ? 'calc(100vh - 140px)' : '360px'};
  overflow: auto; padding: 12px; background: #17202a; color: #eef4fa;
  font: 13px/1.6 monospace; border-radius: 6px;
  &:focus { outline: 3px solid #2283c5; }
`;
const LogLine = styled.div`
  padding: 10px 0; border-bottom: 1px solid #46505c; white-space: nowrap;
  strong { color: ${p => ({send: '#9acaff', recv: '#f2bfd6', event: '#e5d9a6'})[p.direction] || '#eef4fa'}; }
  small { display: block; color: #aeb8c4; font-size: inherit; }
  div { padding-left: 32px; }
`;
export default function DeviceConsole({match: {params: {imei}}}) {
  return <Layout title={`Устройство VEGA · ${imei}`} description="Диагностика, журнал, команды и телеметрия">
    <ConsoleContent key={imei} imei={imei} />
  </Layout>;
}
DeviceConsole.propTypes = {match: PropTypes.shape({params: PropTypes.shape({imei: PropTypes.string.isRequired}).isRequired}).isRequired};
